/**
 * Messages domain on TotalAccessOfflineDB — encrypted payloads via offlinePayloadCrypto.
 * Replaces the legacy TotalAccessMessagingOfflineDB Dexie DB + encryptedStorage dual-write path.
 */
import offlinePayloadCrypto from './offlineCrypto';
import type { OfflineMetaRecord, OfflineRecordSyncStatus } from './offlineDb';
import offlineDb, { type OfflineDomainRecord } from './offlineDb';
import { clearLegacyEncryptedStorageCategory } from './offlineDbMaintenance';

const DOMAIN = 'messages' as const;

export type MessagesStoreName = 'queued_messages' | 'drafts' | 'cached_threads' | 'cached_thread_messages';

export type MessagesMetaKey =
  | 'messaging_sync'
  | 'legacy_migration_v1'
  | 'operations_my_features';

interface UpsertRecordOptions {
  status?: string;
  syncStatus?: OfflineRecordSyncStatus;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

function getDb() {
  return offlineDb;
}

function toIso(value?: Date | string | null, fallback?: string): string {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'string' && value.trim()) {
    return value;
  }
  return fallback || new Date().toISOString();
}

function toEntityId(value: string | number | undefined | null): string {
  return String(value ?? '');
}

function messageToSyncStatus(status: string): OfflineRecordSyncStatus {
  if (status === 'sent') {
    return 'synced';
  }
  if (status === 'failed') {
    return 'failed';
  }
  return 'pending';
}

async function decryptRecordPayload<T>(record: OfflineDomainRecord | undefined): Promise<T | undefined> {
  if (!record) {
    return undefined;
  }
  return offlinePayloadCrypto.decryptValue<T>(record.payload);
}

async function buildRecordRow<T extends Record<string, unknown>>(
  store: MessagesStoreName,
  clientId: number,
  entityId: string,
  item: T,
  existing: OfflineDomainRecord | undefined,
  options?: UpsertRecordOptions & { parentId?: string },
): Promise<OfflineDomainRecord> {
  const now = toIso(options?.updatedAt);
  const payload = await offlinePayloadCrypto.encryptValue(item);
  const explicitStatus = options?.status ?? (item.status as string | undefined);
  const rowStatus = typeof explicitStatus === 'string' ? explicitStatus : undefined;

  return {
    id: existing?.id,
    domain: DOMAIN,
    store,
    client_id: clientId,
    entity_id: entityId,
    parent_id: options?.parentId,
    status: rowStatus,
    sync_status:
      options?.syncStatus ??
      (existing?.sync_status || (rowStatus ? messageToSyncStatus(rowStatus) : 'pending')),
    created_at: existing?.created_at || toIso(options?.createdAt, now),
    updated_at: now,
    payload,
    payload_size: payload.length,
  };
}

function buildMetaStorageKey(clientId: number, key: MessagesMetaKey): string {
  return `${DOMAIN}:${clientId}:${key}`;
}

async function getStoreRow(store: MessagesStoreName, clientId: number, entityId: string): Promise<OfflineDomainRecord | undefined> {
  const db = getDb();
  if (!db) {
    return undefined;
  }
  return db.records
    .where('[domain+store+client_id+entity_id]')
    .equals([DOMAIN, store, clientId, entityId])
    .first();
}

async function getStoreRows(store: MessagesStoreName, clientId: number): Promise<OfflineDomainRecord[]> {
  const db = getDb();
  if (!db) {
    return [];
  }
  return db.records.where('[domain+store+client_id]').equals([DOMAIN, store, clientId]).toArray();
}

const messagesStore = {
  async storeThreads<T extends Record<string, unknown>>(
    clientId: number,
    threads: T[],
  ): Promise<void> {
    const db = getDb();
    if (!db || !clientId) {
      return;
    }

    const existingRows = await getStoreRows('cached_threads', clientId);
    const now = new Date().toISOString();
    const rows = await Promise.all(
      threads
        .map((thread) => {
          const threadId = toEntityId((thread.thread_id as string | number | undefined) ?? (thread.id as string | number | undefined));
          return threadId ? { threadId, thread } : null;
        })
        .filter((item): item is { threadId: string; thread: T } => Boolean(item))
        .map(async ({ threadId, thread }) => {
          const existing = existingRows.find((row) => row.entity_id === threadId);
          return buildRecordRow(
            'cached_threads',
            clientId,
            threadId,
            { ...thread, _cachedAt: Date.now() },
            existing,
            { syncStatus: 'synced', updatedAt: now },
          );
        }),
    );

    const incomingIds = new Set(rows.map((row) => row.entity_id));
    const staleIds = existingRows
      .filter((row) => !incomingIds.has(row.entity_id))
      .map((row) => row.id)
      .filter((id): id is number => typeof id === 'number');

    await db.transaction('rw', db.records, async () => {
      if (staleIds.length > 0) {
        await db.records.bulkDelete(staleIds);
      }
      if (rows.length > 0) {
        await db.records.bulkPut(rows);
      }
    });
  },

  async listCachedThreads<T>(clientId: number): Promise<T[]> {
    const rows = await getStoreRows('cached_threads', clientId);
    const values = await Promise.all(rows.map((row) => decryptRecordPayload<T & { _cachedAt?: number }>(row)));
    const out: Array<T & { _cachedAt?: number }> = [];
    for (const value of values) {
      if (value !== undefined) {
        out.push(value as T & { _cachedAt?: number });
      }
    }
    const getSortTime = (thread: T & { _cachedAt?: number }) => {
      const sortable = thread as T & {
        last_message_at?: unknown;
        updated_at?: unknown;
        created_at?: unknown;
        _cachedAt?: number;
      };
      return new Date(String(sortable.last_message_at || sortable.updated_at || sortable.created_at || sortable._cachedAt || 0)).getTime();
    };
    return out.sort((a, b) => getSortTime(b) - getSortTime(a)) as T[];
  },

  async upsertThreadMessages<T extends Record<string, unknown>>(
    clientId: number,
    threadId: string,
    messages: T[],
  ): Promise<void> {
    const db = getDb();
    if (!db || !clientId || !threadId) {
      return;
    }
    const existing = await getStoreRow('cached_thread_messages', clientId, threadId);
    const row = await buildRecordRow(
      'cached_thread_messages',
      clientId,
      threadId,
      {
        thread_id: threadId,
        messages,
        _cachedAt: Date.now(),
      },
      existing,
      { parentId: threadId, syncStatus: 'synced' },
    );
    await db.records.put(row);
  },

  async getCachedThreadMessages<T>(clientId: number, threadId: string): Promise<T[]> {
    if (!clientId || !threadId) {
      return [];
    }
    const row = await getStoreRow('cached_thread_messages', clientId, threadId);
    const payload = await decryptRecordPayload<{ messages?: T[] }>(row);
    return Array.isArray(payload?.messages) ? payload.messages : [];
  },

  async upsertQueuedMessage<T extends Record<string, unknown>>(
    clientId: number,
    offlineId: string,
    item: T,
    threadId: string,
    options?: UpsertRecordOptions,
  ): Promise<void> {
    const db = getDb();
    if (!db || !clientId) {
      return;
    }
    const existing = await getStoreRow('queued_messages', clientId, offlineId);
    const row = await buildRecordRow('queued_messages', clientId, offlineId, item, existing, {
      parentId: threadId,
      ...options,
    });
    await db.records.put(row);
  },

  async getQueuedMessage<T>(clientId: number, offlineId: string): Promise<T | undefined> {
    const row = await getStoreRow('queued_messages', clientId, offlineId);
    return decryptRecordPayload<T>(row);
  },

  async deleteQueuedMessage(clientId: number, offlineId: string): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    const row = await getStoreRow('queued_messages', clientId, offlineId);
    if (row?.id) {
      await db.records.delete(row.id);
    }
  },

  async listQueuedMessages<T>(clientId: number): Promise<T[]> {
    const rows = await getStoreRows('queued_messages', clientId);
    const values = await Promise.all(rows.map((row) => decryptRecordPayload<T>(row)));
    return values.reduce<T[]>((acc, v) => {
      if (v !== undefined) {
        acc.push(v);
      }
      return acc;
    }, []);
  },

  async listQueuedMessagesForThread<T>(clientId: number, threadId: string): Promise<T[]> {
    const db = getDb();
    if (!db) {
      return [];
    }
    const rows = await db.records
      .where('[domain+store+client_id+parent_id]')
      .equals([DOMAIN, 'queued_messages', clientId, threadId])
      .toArray();
    const values = await Promise.all(rows.map((row) => decryptRecordPayload<T>(row)));
    return values.reduce<T[]>((acc, v) => {
      if (v !== undefined) {
        acc.push(v);
      }
      return acc;
    }, []);
  },

  async bulkDeleteQueuedSentBefore(clientId: number, cutoffIso: string): Promise<number> {
    const items = await this.listQueuedMessages<{ status?: string; sent_at?: Date | string } & Record<string, unknown>>(
      clientId,
    );
    let deleted = 0;
    for (const item of items) {
      if (item.status !== 'sent' || item.sent_at === undefined) {
        continue;
      }
      const sent =
        item.sent_at instanceof Date ? item.sent_at : new Date(String(item.sent_at));
      if (sent < new Date(cutoffIso)) {
        const oid = (item as { offline_id?: string }).offline_id;
        if (oid) {
          await this.deleteQueuedMessage(clientId, oid);
          deleted += 1;
        }
      }
    }
    return deleted;
  },

  /** Draft entity id: user-scoped thread key */
  draftEntityId(userId: number, threadId: string): string {
    return `u${userId}:t${threadId}`;
  },

  async upsertDraft<T extends Record<string, unknown>>(
    clientId: number,
    entityId: string,
    item: T,
    options?: UpsertRecordOptions,
  ): Promise<void> {
    const db = getDb();
    if (!db || !clientId) {
      return;
    }
    const existing = await getStoreRow('drafts', clientId, entityId);
    const row = await buildRecordRow('drafts', clientId, entityId, item, existing, options);
    await db.records.put(row);
  },

  async getDraft<T>(clientId: number, entityId: string): Promise<T | undefined> {
    const row = await getStoreRow('drafts', clientId, entityId);
    return decryptRecordPayload<T>(row);
  },

  async deleteDraft(clientId: number, entityId: string): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    const row = await getStoreRow('drafts', clientId, entityId);
    if (row?.id) {
      await db.records.delete(row.id);
    }
  },

  async listDraftsForUser<T extends { user_id?: number; updated_at?: Date | string }>(
    clientId: number,
    userId: number,
  ): Promise<T[]> {
    const rows = await getStoreRows('drafts', clientId);
    const values = await Promise.all(rows.map((row) => decryptRecordPayload<T>(row)));
    const out: T[] = [];
    for (const v of values) {
      if (v !== undefined && v.user_id === userId) {
        out.push(v);
      }
    }
    return out.sort((a, b) => {
      const ua = new Date(String(a.updated_at || 0)).getTime();
      const ub = new Date(String(b.updated_at || 0)).getTime();
      return ub - ua;
    });
  },

  async getDraftByDraftId<T extends { draft_id?: string }>(clientId: number, draftId: string): Promise<T | undefined> {
    const rows = await getStoreRows('drafts', clientId);
    for (const row of rows) {
      const d = await decryptRecordPayload<T>(row);
      if (d && d.draft_id === draftId) {
        return d;
      }
    }
    return undefined;
  },

  async deleteDraftByDraftId(clientId: number, draftId: string): Promise<void> {
    const rows = await getStoreRows('drafts', clientId);
    for (const row of rows) {
      const d = await decryptRecordPayload<{ draft_id?: string }>(row);
      if (d?.draft_id === draftId && row.id) {
        const db = getDb();
        await db?.records.delete(row.id);
        return;
      }
    }
  },

  async countDraftsForUser(clientId: number, userId: number): Promise<number> {
    const drafts = await this.listDraftsForUser< { user_id?: number } >(clientId, userId);
    return drafts.length;
  },

  async setMeta<T>(clientId: number, key: MessagesMetaKey, value: T): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    const row: OfflineMetaRecord = {
      key: buildMetaStorageKey(clientId, key),
      domain: DOMAIN,
      client_id: clientId,
      updated_at: new Date().toISOString(),
      payload: await offlinePayloadCrypto.encryptValue(value),
    };
    await db.meta.put(row);
  },

  async getMeta<T>(clientId: number, key: MessagesMetaKey): Promise<T | null> {
    const db = getDb();
    if (!db) {
      return null;
    }
    const row = await db.meta.get(buildMetaStorageKey(clientId, key));
    if (!row) {
      return null;
    }
    return offlinePayloadCrypto.decryptValue<T>(row.payload);
  },

  async deleteMeta(clientId: number, key: MessagesMetaKey): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    await db.meta.delete(buildMetaStorageKey(clientId, key));
  },

  async clearClient(clientId: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    const [recordRows, metaRows] = await Promise.all([
      db.records.where('[domain+client_id]').equals([DOMAIN, clientId]).toArray(),
      db.meta.where('[domain+client_id]').equals([DOMAIN, clientId]).toArray(),
    ]);
    const recordIds = recordRows.map((r) => r.id).filter((id): id is number => typeof id === 'number');
    const metaKeys = metaRows.map((r) => r.key);
    await db.transaction('rw', db.records, db.meta, async () => {
      if (recordIds.length > 0) {
        await db.records.bulkDelete(recordIds);
      }
      if (metaKeys.length > 0) {
        await db.meta.bulkDelete(metaKeys);
      }
    });
  },
};

/** One-time migration from TotalAccessMessagingOfflineDB + strip legacy encrypted keys */
export async function migrateLegacyMessagingDatabaseIfNeeded(): Promise<void> {
  if (typeof window === 'undefined' || !offlineDb) {
    return;
  }
  const marker = await messagesStore.getMeta(0, 'legacy_migration_v1');
  if (marker) {
    return;
  }

  try {
    const DexieMod = await import('dexie');
    const Dexie = DexieMod.default;

    let legacyRows: Record<string, unknown>[] = [];
    let legacyDrafts: Record<string, unknown>[] = [];
    try {
      const legacyDb = new Dexie('TotalAccessMessagingOfflineDB');
      legacyDb.version(2).stores({
        messages:
          '++id, offline_id, client_id, user_id, thread_id, status, created_at, queued_at, [client_id+thread_id]',
        drafts: '++id, thread_id, client_id, user_id, updated_at, [thread_id+client_id+user_id]',
        syncMetadata: 'id',
      });
      await legacyDb.open();
      legacyRows = (await legacyDb.table('messages').toArray()) as Record<string, unknown>[];
      legacyDrafts = (await legacyDb.table('drafts').toArray()) as Record<string, unknown>[];
      await legacyDb.close();
      await Dexie.delete('TotalAccessMessagingOfflineDB');
    } catch {
      // Legacy DB absent or unreadable
    }

    for (const d of legacyDrafts) {
      const cid = Number(d.client_id || 0);
      const uid = Number(d.user_id || 0);
      const tid = String(d.thread_id || '');
      if (!cid || !tid) {
        continue;
      }
      const eid = messagesStore.draftEntityId(uid || 0, tid);
      await messagesStore.upsertDraft(cid, eid, d as Record<string, unknown>);
    }

    interface LegacyMsg {
      offline_id?: string;
      client_id?: number;
      thread_id?: string;
      [key: string]: unknown;
    }

    const clientIds = new Set<number>();
    for (const row of legacyRows) {
      const r = row as LegacyMsg;
      const cid = Number(r.client_id || 0);
      const oid = String(r.offline_id || '');
      if (!cid || !oid) {
        continue;
      }
      clientIds.add(cid);
      const tid = String(r.thread_id || '');
      await messagesStore.upsertQueuedMessage(cid, oid, r as Record<string, unknown>, tid, {});
    }

    for (const cid of clientIds) {
      await clearLegacyEncryptedStorageCategory('messages', cid).catch(() => undefined);
    }

    await messagesStore.setMeta(0, 'legacy_migration_v1', {
      migrated_at: new Date().toISOString(),
      rows: legacyRows.length + legacyDrafts.length,
    });
  } catch (e) {
    console.warn('[messagesStore] Legacy migration skipped:', e);
  }
}

export default messagesStore;
